import { isEmpty, pickBy } from 'lodash';
import { identity } from 'lodash/fp';
// Constants
const adjustHost = 'https://fgjq.adj.st';
const adjustLinkToken = '1osfh5nh';
// Reads a single cookie value by name. Client-side only: returns '' during SSR
// (document is undefined) or when the cookie is absent. Value is returned raw —
// the Facebook Pixel does not URL-encode _fbp/_fbc, and generateAdjustLabel()
// encodes everything before it goes into the link.
function readCookie(name) {
    if (typeof document === 'undefined')
        return '';
    const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1');
    const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + escaped + '=([^;]*)'));
    return match ? match[1] : '';
}
// Collects the Facebook browser identifiers needed for Conversions API (CAPI)
// attribution of paid Meta traffic on the web→app flow:
//   - fbp    : the _fbp cookie set by the Pixel (format fb.1.<ms>.<random>)
//   - fbc    : the _fbc cookie set by the Pixel from fbclid (format fb.1.<ts>.<fbclid>)
//   - fbclid : the raw fbclid query param — a fallback sent only when _fbc is
//              missing (an existing _fbc already contains the fbclid, and the
//              often-long fbclid would needlessly bloat the label)
//   - ua     : the browser user agent (CAPI client_user_agent, required for
//              action_source=website events). Meta matches it against the
//              browser that owns fbp/fbc, so it must come from the same page
//              view — an Adjust-side or backend-side user agent would belong
//              to the install, not to this browser.
//   - url    : the landing page URL (CAPI event_source_url, also required for
//              website events). origin + pathname only: the label format joins
//              pairs with a literal %26, and encodeURIComponent turns a query
//              string's own '&' into the same %26 — embedding it would make the
//              value indistinguishable from the separators and break parsing.
//   - utm_*  : the visit's utm params as separate label keys instead (they are
//              short and '&'-free). Meta doesn't match on them, but Events
//              Manager URL rules (custom conversions) and campaign debugging
//              can use them — the backend can rebuild a full event_source_url
//              as url + '?' + utm pairs when needed.
// Runs on the client only (returns {} during SSR). Only non-empty values are
// returned, so empty keys are never appended to the Adjust label.
function getFacebookParams() {
    if (typeof window === 'undefined' || typeof document === 'undefined')
        return {};
    const fbc = readCookie('_fbc');
    const identifiers = pickBy({
        fbp: readCookie('_fbp'),
        fbc,
        fbclid: fbc ? '' : new URLSearchParams(window.location.search).get('fbclid') || '',
    }, identity);
    if (isEmpty(identifiers))
        return identifiers;
    // On their own the user agent, page URL and utm params attribute nothing, so
    // they ride along only when there is an identifier to match them with —
    // otherwise every Adjust link on the site (invites included) would carry a
    // few hundred wasted characters.
    const query = new URLSearchParams(window.location.search);
    return Object.assign(Object.assign(Object.assign({}, identifiers), { ua: navigator.userAgent, url: window.location.origin + window.location.pathname }), pickBy({
        utm_source: query.get('utm_source') || '',
        utm_medium: query.get('utm_medium') || '',
        utm_campaign: query.get('utm_campaign') || '',
        utm_content: query.get('utm_content') || '',
        utm_term: query.get('utm_term') || '',
    }, identity));
}
// Resolves once the Pixel has had its chance to write the _fbp/_fbc cookies,
// so an Adjust link built afterwards carries them: immediately when _fbp is
// already present or fbevents.js has loaded (the real script sets
// fbq.callMethod on the inline stub; the cookie may still legitimately never
// appear — ad blocker, consent), otherwise polls up to maxWaitMs. Bounds the
// worst-case click delay instead of navigating with whatever happens to be set.
export function whenFacebookParamsReady(maxWaitMs = 500) {
    return new Promise((resolve) => {
        if (typeof window === 'undefined')
            return resolve();
        const pixelReady = () => { var _a; return Boolean(readCookie('_fbp') || ((_a = window.fbq) === null || _a === void 0 ? void 0 : _a.callMethod)); };
        if (pixelReady())
            return resolve();
        const startedAt = Date.now();
        const timer = setInterval(() => {
            if (pixelReady() || Date.now() - startedAt >= maxWaitMs) {
                clearInterval(timer);
                resolve();
            }
        }, 50);
    });
}
// Utility functions
function generateAdjustLabel(params) {
    return Object.entries(params)
        .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
        .join('%26');
}
export function generateAdjustUrl(label, deeplink = '') {
    const path = deeplink ? `/${deeplink}` : '';
    return `${adjustHost}${path}?adj_t=${adjustLinkToken}&adj_label=${encodeURIComponent(label)}`;
}
// Core Adjust functions
export function generateAdjustInviteQuery(user, options) {
    // Extract all parameters from deeplink_path if it exists
    let deeplinkPath = options.deeplink_path || '';
    const pathParams = {};
    if (deeplinkPath) {
        const [path, queryString] = deeplinkPath.split('?');
        if (queryString) {
            const urlParams = new URLSearchParams(queryString);
            urlParams.forEach((value, key) => {
                pathParams[key] = value;
            });
        }
        // Remove the query parameters from deeplink_path
        deeplinkPath = path;
    }
    const params = pickBy(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({ inviter_id: pathParams.inviter_id || (user === null || user === void 0 ? void 0 : user.id) || '', inviter_name: (user === null || user === void 0 ? void 0 : user.fullname) || '', channel: options.channel || 'other', feature: options.feature || 'invite', campaign: options.campaign }, (options.challenge_id
        ? {
            challenge_id: options.challenge_id,
            deeplink_path: `/walk-with-friends/${options.challenge_id}`,
            feature: 'challenge_invite',
        }
        : {})), (options.group_id
        ? {
            group_id: options.group_id,
            deeplink_path: `/group/${options.group_id}`,
            feature: 'group_invite',
        }
        : {})), (options.welcome
        ? {
            deeplink_path: '/onboarding-popup',
        }
        : {})), { deeplink_path: deeplinkPath }), pathParams), getFacebookParams()), identity);
    const label = generateAdjustLabel(params);
    const deeplink = params.deeplink_path ? params.deeplink_path.replace(/^\//, '') : '';
    return generateAdjustUrl(label, deeplink);
}
export const generateAdjustLink = ({ inviterId, inviterName, channel = 'other', feature = 'invite', campaign, deeplink_path, }) => {
    const params = Object.assign({ inviter_id: inviterId, inviter_name: inviterName, channel,
        feature,
        campaign,
        deeplink_path }, getFacebookParams());
    const label = generateAdjustLabel(params);
    return generateAdjustUrl(label, deeplink_path);
};
